import React, { useEffect, useState } from "react";

/**
 * SmartImage — drop-in <img> replacement that NEVER shows a broken-image
 * icon. Two failure modes are handled:
 *
 *   1. ``src`` is empty / null / undefined  →  render a CSS-generated
 *      initial-letter tile (or the supplied ``fallback`` node).
 *   2. ``src`` loads but 404s / blocks / corrupts at runtime  →  the
 *      onError handler swaps the rendering to the same fallback tile
 *      without a network round-trip.
 *
 * The tile uses a vermilion gradient by default so it visually reads as
 * "intentional design" rather than "missing image", matching the 1o1
 * theme. A custom gradient can be passed via ``tileStyle``.
 *
 * Props
 * ─────
 *   src          {string}     — image URL (may be empty / null / 404'ing)
 *   alt          {string}     — accessibility label
 *   seed         {string}     — text used to derive the initial letter
 *                                (e.g. the artist name or track title)
 *   className    {string}     — applied to the OUTER wrapper (sizing!)
 *   imgClassName {string}     — applied to the <img> when src is valid
 *   fallback     {node}       — optional override for the empty-state tile
 *   tileStyle    {object}     — extra style on the fallback tile
 *   onLoadOk     {() => void} — fires the first time the image actually loads
 *
 * Performance
 * ───────────
 *   Re-renders only when ``src`` changes. We track a single ``broken``
 *   state — once true, the <img> is unmounted so it can't keep retrying.
 *   When ``src`` changes to a new value, broken resets so the new URL
 *   gets a fair shot.
 */
const SmartImage = ({
  src,
  alt = "",
  seed = "",
  className = "",
  imgClassName = "",
  fallback,
  tileStyle,
  onLoadOk,
  loading = "lazy",
  draggable,
}) => {
  const [broken, setBroken] = useState(false);

  // Reset broken state when src changes — a new URL deserves a fresh attempt.
  useEffect(() => {
    setBroken(false);
  }, [src]);

  const showTile = !src || broken;
  const initial = (seed || alt || "?").trim().charAt(0).toUpperCase() || "?";

  return (
    <div className={`relative ${className}`}>
      {showTile ? (
        fallback ?? (
          <div
            aria-label={alt}
            className="absolute inset-0 flex items-center justify-center overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, rgba(232,69,37,0.95) 0%, rgba(232,69,37,0.65) 45%, rgba(232,69,37,0.4) 100%)",
              ...tileStyle,
            }}
          >
            <span
              aria-hidden="true"
              className="font-display font-black text-white/95 select-none leading-none"
              style={{ fontSize: "55%", lineHeight: 1 }}
            >
              {initial}
            </span>
          </div>
        )
      ) : (
        <img
          src={src}
          alt={alt}
          loading={loading}
          draggable={draggable}
          onError={() => setBroken(true)}
          onLoad={onLoadOk}
          className={imgClassName || "absolute inset-0 w-full h-full object-cover"}
        />
      )}
    </div>
  );
};

export default SmartImage;
